import { BlockDiagram } from '@cadstrom/cadstrom-shared';
import CircularProgress from '@mui/material/CircularProgress';
import { useMemo } from 'react';

import { ColorSchemes } from '#components/cad-renderer/color-provider/cadstrom-color-provider.js';
import { ProjectModelEmbed } from '#components/cad-renderer/project-model-embed.js';
import { BlockDiagramLegend } from './block-diagram-legend.js';
import { mergeBlockDiagramColors } from './merge-block-diagram-colors.js';
import { useCompiledBlockDiagram } from './use-compiled-block-diagram.js';

import type { DesignModels } from '@cadstrom/sigma-engine-shared';

export type BlockDiagramViewerProps = {
	/** Revision (build) the diagram belongs to. */
	revisionId: string;
	/** The stored block-diagram document. */
	document: BlockDiagram.BlockDiagramDocument;
	/** Which projection to display. */
	variant: BlockDiagram.BlockDiagramVariant;
	/** View-time layout/style adjustments. */
	knobs?: BlockDiagram.BlockDiagramKnobs;
	/**
	 * Design to select initially within the compiled model. Defaults to the
	 * block-diagram design (currently the only one a compiled document has).
	 */
	designId?: string;
	/** When set, the initial view zooms to this box instead of the whole diagram. */
	bbox?: DesignModels.ModelBoundingBox;
	/** Hides the renderer's history navigator and design selector. */
	hideNavigationControls?: boolean;
	/** Hides the renderer's tools column. Defaults to hidden. */
	hideToolbar?: boolean;
	/** Hides the renderer's status bar. Defaults to hidden. */
	hideStatusBar?: boolean;
	className?: string;
};

/**
 * Compiles a block-diagram document at view time and renders it through the
 * generic project-model embed — a block diagram is one special case of a
 * project-model render, with the semantic block-diagram colour scheme merged
 * in. Compilation runs in a web worker so large designs never stall the UI;
 * the previous frame is kept while the document, variant, or knobs recompile.
 * Theming follows the active colour scheme via the semantic layers. By
 * default the render is chrome-free: no grid, and the renderer's toolbar and
 * status bar hidden.
 *
 * @param props - Component props.
 */
export function BlockDiagramViewer({
	revisionId,
	document,
	variant,
	knobs,
	designId,
	bbox,
	hideNavigationControls,
	hideToolbar = true,
	hideStatusBar = true,
	className,
}: BlockDiagramViewerProps) {
	const compiledState = useCompiledBlockDiagram(document, variant, knobs);
	const colorScheme = useMemo(() => mergeBlockDiagramColors(ColorSchemes.Default), []);

	if (compiledState === null) {
		return (
			<div className={className}>
				<div className='grid h-full w-full place-items-center'>
					<CircularProgress size={24} />
				</div>
			</div>
		);
	}

	return (
		<ProjectModelEmbed
			key={compiledState.variant}
			revisionId={revisionId}
			projectModel={compiledState.compiled.projectModel}
			designId={designId ?? BlockDiagram.BLOCK_DIAGRAM_DESIGN_ID}
			bbox={bbox}
			colorScheme={colorScheme}
			hideNavigationControls={hideNavigationControls}
			hideToolbar={hideToolbar}
			hideStatusBar={hideStatusBar}
			defaultGridType='none'
			canvasOverlay={
				<div className='absolute right-2 bottom-2 select-none'>
					<BlockDiagramLegend voltageScale={compiledState.compiled.voltageScale} />
				</div>
			}
			className={className}
		/>
	);
}
